import React from 'react';

import { PermissionContext } from '@cvt/contexts';

import { AuthContext } from '@modules/Auth/contexts';

import { UserType } from '../../enums';

import { ContextProps } from './types';


export const defaultContext: ContextProps = {
  user: undefined,
  isAdmin: false,
  isHomeowner: false,
  isPartner: false,
};

// Admin sees every current section of the app. Homeowner and Partner have no
// access to the current pages (they land on the Homeowner Hub / a future
// partner board), so their permission lists are intentionally empty for now.
const adminPermissionList: CVT.Permission.PermissionKey[] = [
  'users.list',
  'users.view',
  'users.create',
  'users.edit',
  'users.delete',
  'users.invite',
  'companies.list',
  'companies.view',
  'companies.create',
  'companies.edit',
  'companies.delete',
  'projects.list',
  'projects.view',
  'projects.create',
  'projects.edit',
  'projects.delete',
  'leads.list',
  'leads.view',
  'leads.create',
  'leads.edit',
  'leads.delete',
];
const homeownerPermissionList: CVT.Permission.PermissionKey[] = [
  'hub.view',
];
const partnerPermissionList: CVT.Permission.PermissionKey[] = [];

export const UserContext = React.createContext(defaultContext);

export const UserProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

  const { user } = React.useContext(AuthContext);
  const { updatePermissions } = React.useContext(PermissionContext);

  const isAdmin = React.useMemo(() => user?.type === UserType.Admin, [user]);
  const isHomeowner = React.useMemo(() => user?.type === UserType.Homeowner, [user]);
  const isPartner = React.useMemo(() => user?.type === UserType.Partner, [user]);

  React.useEffect(() => {
    // Grant the active role's permissions and revoke the others so switching
    // accounts always resets cleanly on top of config.defaultPermissions.
    updatePermissions(adminPermissionList, isAdmin);
    updatePermissions(homeownerPermissionList, isHomeowner);
    updatePermissions(partnerPermissionList, isPartner);
  }, [updatePermissions, isAdmin, isHomeowner, isPartner]);

  return (
    <UserContext.Provider
      value={{
        user,
        isAdmin,
        isHomeowner,
        isPartner,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserConsumer = UserContext.Consumer;
